import { LocalDate, LocalTime } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn, scheduleForCleanup } from "trader.ts/ui";
import { renderIf } from "trader.ts/ui.common";
import {
  Field,
  Form,
  checkBox,
  constantField,
  dateBox,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulaid,
  formulaquestionid,
  iban,
  instrumentid,
  internalteacherid,
  lessongroupid,
  price_adapted_to_lg,
  price_adapted_to_lg_and_student,
  price_formula,
  questiontype,
  studentid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeArrayField, oneLineArrayRender } from "../../client/arrayfield";
import { checkboxesField } from "../../client/checkboxesfield";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import { questionsField } from "../../client/questionsField";
import {
  radioField,
  radioFieldCheckboxRenderInputLike,
} from "../../client/radiofield";
import { renderErrorPanel, renderSaveButton } from "../../client/savebutton";
import {
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import {
  accountInfoClient,
  invoicelineEachPeriod,
  invoicelineFormula,
  invoicelineLG,
  invoicelineRequestedOptional,
  invoicelineStudent,
} from "../../domain/finance/types";
import { sumCurrency } from "../../lib/currency";
import { emailValidation } from "../../lib/emailisvalid";
import { isNil } from "../../lib/istype";
import { touchAllInputs } from "../../lib/touchallinputs";
import {
  ibanValidation,
  mandatoryValidation,
  nameValidation,
  phoneValidationSimple,
  streetValidation,
  zipValidation,
} from "../../lib/validate";
import {
  allPhoneTypes,
  invoicelinetype,
  phoneType,
} from "../../parsing_from_ur";
import {
  renderCurrency,
  renderFirstNameLastName,
  renderPaymentScheme,
  wrapInParentheses,
} from "../../render";
import * as routes from "../../routes";
import { translatePhoneType } from "../../translations_specific";
import { errorField } from "../planning/lessongroupform";
import { stringToJsxClientside } from "../editemailtemplates/client";

export type answeredQuestion = {
  formulaquestionid: formulaquestionid;
  label: string;
  questiontype: questiontype;
  text_answer: string | null;
  number_answer: number | null;
  boolean_answer: boolean | null;
  select_answer: string | null;
  multiselect_answers: string[] | null;
  ordering: number;
};
export type formulaQuestion = {
  id: formulaquestionid;
  label: string;
  questiontype: questiontype;
  mandatory: boolean;
  ordering: number;
  select_options: string[];
};

type mode =
  | { tag: "NewOrExisting" }
  | { tag: "New" }
  | {
      tag: "Existing";
      students: { id: studentid; firstname: string; lastname: string }[];
    };

const modeC = routes.magicCodec<mode>();
const modeRoute = makeRoute("/selection/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});

const router = makeClientsideRouter(modeRoute, {
  tag: "NewOrExisting" as const,
});

export function render(opts: {
  schoolname: string;
  termsurl: null | string;
  max_one_optional_line: boolean;
  hide_invoicelines: boolean;
  requireConfirmationForVisualMediaPublicity: boolean;
  mandatoryInvoiceLines: (
    | invoicelineFormula
    | invoicelineLG
    | (invoicelineStudent & { type_: invoicelinetype })
  )[];
  optionalInvoiceLines: (
    | invoicelineFormula
    | invoicelineLG
    | (invoicelineStudent & { type_: invoicelinetype })
  )[];
  questions: formulaQuestion[];
  possiblePaymentSchemes: (
    | periodic_payment_scheme
    | non_periodic_payment_scheme
  )[];
  students: null | { id: studentid; firstname: string; lastname: string }[];
  target:
    | {
        tag: "lessongroup";
        trial_lesson: boolean;
        id: lessongroupid;
      }
    | {
        tag: "private";
        starttime: LocalTime;
        startdate: LocalDate;
        formulaid: formulaid;
        instrumentid: instrumentid;
        internalteacherid: internalteacherid | undefined;
      };
}): HTMLElement | HTMLElement[] {
  const modeS = router.start();

  if (opts.students !== null) {
    modeS.set({ tag: "Existing", students: opts.students });
  }

  const loginRoutePrefilled = routes.loginRoute.link({
    logoutfirst: true,
    redirectdescription: "Log in om je inschrijving verder te zetten",
    redirecturl: document.location.toString(),
  });

  return dyn(modeS, function (mode) {
    if (mode.tag === "NewOrExisting") {
      return (
        <div
          className={style.equal_columns}
          style="padding: 8px; align-items: stretch; "
        >
          <a
            className={style.a_around_button}
            href={router.makeHashLink({ tag: "New" })}
          >
            <button
              className={style.button_accent}
              style="width: 100%; height: 4rem;"
            >
              {"Ik ben nieuw bij %schoolname".replace(
                "%schoolname",
                opts.schoolname,
              )}
            </button>
          </a>
          <a className={style.a_around_button} href={loginRoutePrefilled}>
            <button style="width: 100%; height: 4rem;">
              {"Ik heb al een account bij %schoolname".replace(
                "%schoolname",
                opts.schoolname,
              )}
            </button>
          </a>
        </div>
      );
    } else if (mode.tag === "New") {
      const dataForm = makeNewStudentDataForm(opts);
      const showFinancialForm =
        opts.optionalInvoiceLines.length > 0 ||
        opts.possiblePaymentSchemes.length > 1 ||
        opts.possiblePaymentSchemes.some(
          (ps) =>
            ps.tag === "periodic" &&
            ps.periodic_payment_method === "direct_debit",
        );
      const financialForm = makeNewStudentFinancialForm(opts, {
        showFinancialForm,
      });
      const loginForm = makeNewStudentLoginForm({
        showFinancialForm,
      });
      const questionsForm = new Form({})
        .addField("questions", [], () =>
          questionsField<{ formulaquestionid: formulaquestionid }>(
            opts.questions.map((q) => ({
              ...q,
              formulaquestionid: q.id,
              number_answer: null,
              text_answer: null,
              boolean_answer: null,
              select_answer: null,
              multiselect_answers: null,
            })),
          ),
        )
        .build({
          render: (fields) => (
            <section>
              {opts.questions.length > 0 ? (
                <h3 style="margin-top: -16px;margin-bottom: 32px; font-weight: 600;">
                  {"... en nog wat vragen ..."}
                </h3>
              ) : (
                <span></span>
              )}
              {fields.questions()}
            </section>
          ),
        });
      const markRequiredInputsS = new Source(false);
      scheduleForCleanup(
        markRequiredInputsS.observe((val) => {
          if (val === true) {
            touchAllInputs();
          }
        }),
      );
      dataForm.s.observe((df) => {
        console.log(JSON.stringify(df, null, 2));
      });

      const saveButton = renderSaveButton({
        save: async function save() {
          const dataValuesP = dataForm.s.get();
          const questionValuesP = questionsForm.s.get();
          const financialValuesP = financialForm.s.get();
          const loginValuesP = loginForm.s.get();
          if (
            dataValuesP.tag !== "parsed" ||
            questionValuesP.tag !== "parsed" ||
            financialValuesP.tag !== "parsed" ||
            loginValuesP.tag !== "parsed"
          ) {
            markRequiredInputsS.set(true);
            return {
              error: "Je mist nog een of meer verplichte velden",
            };
          } else {
            markRequiredInputsS.set(false);
          }
          const dataValues = dataValuesP.parsed;
          const loginValues = loginValuesP.parsed;
          const financialValues = financialValuesP.parsed;
          const questionValues = questionValuesP.parsed;

          const emails = [loginValues.email]
            .concat(loginValues.extra_emails)
            .map((em) => em.trim())
            .uniq();
          const comments =
            dataValues.comments +
            (financialValues.familyDiscountComments.trim()
              ? "\n" +
                "Familie" +
                ": " +
                financialValues.familyDiscountComments
              : "");

          const res = await rpc(routes.saveNewPublicEnrollmentSpec, undefined, {
            target: opts.target,
            questions: questionValues.questions,
            student: {
              tag: "new" as const,
              ...dataValues,
              ...financialValues,
              ...loginValues,
              payment_scheme_id: financialValues.paymentScheme.id,
              comments,
              emails,
            },
          });

          return handleSaveNewPublicEnrollmentNewStudentResult(res);
        },
        timeout: 60 * 60 * 1000,
        reload: false,
      });

      const subFormShownS: Source<
        "data_login" | "data" | "questions" | "login"
      > = new Source(opts.questions.length > 0 ? "data" : "data_login");

      const alreadyStudentSection = (
        <section style="text-align: right;">
          <a
            href={loginRoutePrefilled}
            style="white-space: pre-line; display: block;"
          >
            {"Is de persoon die je inschrijft al gekend bij ons? \nLog dan hier in."}
          </a>
        </section>
      );

      const someQuestionsAreNotFilledOutSection = renderIf(
        markRequiredInputsS,
        () => (
          <section style="text-align: right">
            {renderErrorPanel({
              error: "Je mist nog een of meer verplichte velden",
            })}
          </section>
        ),
      );

      return (
        <div>
          {
            <div>
              {dyn(subFormShownS, (subFormShown) => {
                if (subFormShown === "data_login") {
                  return (
                    <div className={style.equal_columns}>
                      <div>
                        {dataForm.render()}
                        {questionsForm.render() /* should be empty */}
                      </div>
                      <div>
                        {financialForm.render()}
                        {loginForm.render()}
                        <section style="text-align: right; ">
                          {saveButton}
                        </section>
                        {alreadyStudentSection}
                      </div>
                    </div>
                  );
                } else if (subFormShown === "data") {
                  return (
                    <div>
                      {dataForm.render()}
                      <section style="text-align: right">
                        <button
                          onclick={() => {
                            if (dataForm.s.get().tag === "parsed") {
                              markRequiredInputsS.set(false);
                              subFormShownS.set("questions");
                            } else {
                              markRequiredInputsS.set(true);
                            }
                          }}
                        >
                          {"Ga verder"}
                        </button>
                      </section>
                      {alreadyStudentSection}
                      {someQuestionsAreNotFilledOutSection}
                    </div>
                  );
                } else if (subFormShown === "questions") {
                  return (
                    <div>
                      {questionsForm.render()}
                      <section style="text-align: right">
                        <button onclick={() => subFormShownS.set("data")}>
                          {"Ga terug"}
                        </button>
                        <button
                          onclick={() => {
                            if (questionsForm.s.get().tag === "parsed") {
                              markRequiredInputsS.set(false);
                              subFormShownS.set("login");
                            } else {
                              markRequiredInputsS.set(true);
                            }
                          }}
                        >
                          {"Ga verder"}
                        </button>
                      </section>
                      {someQuestionsAreNotFilledOutSection}
                    </div>
                  );
                } else if (subFormShown === "login") {
                  return (
                    <div>
                      {financialForm.render()}
                      {loginForm.render()}
                      <section style="text-align: right">
                        <section style="text-align: right; ">
                          <button
                            onclick={() => subFormShownS.set("questions")}
                          >
                            {"Ga terug"}
                          </button>
                          {saveButton}
                        </section>
                        {alreadyStudentSection}
                      </section>
                    </div>
                  );
                } else {
                  return checkAllCasesHandled(subFormShown);
                }
              })}
            </div>
          }
        </div>
      );
    } else if (mode.tag === "Existing") {
      const form = new Form({})
        .addField("student", [], async () => {
          if (mode.students.length === 1) {
            return constantField(mode.students[0]);
          } else {
            return radioField({
              initial: mode.students[0],
              values: mode.students,
              show: (s) => renderFirstNameLastName(s),
              render: (s, selected, onclick) => {
                return (
                  <a onclick={onclick} style="margin-bottom: 16px;">
                    <div
                      className={
                        style.card +
                        style.card_with_icon +
                        (selected ? style.is_selected : "")
                      }
                    >
                      <i className={FA.icon_user_solid}></i>
                      {s}
                    </div>
                  </a>
                );
              },
              label: "Student",
              containerClass: style.grid_card_small,
            });
          }
        })
        .addField("questions", [], () =>
          questionsField<{ formulaquestionid: formulaquestionid }>(
            opts.questions.map((q) => ({
              ...q,
              formulaquestionid: q.id,
              number_answer: null,
              text_answer: null,
              boolean_answer: null,
              select_answer: null,
              multiselect_answers: null,
            })),
          ),
        )
        .addField("optionalInvoiceLinesSelected", [], async () => {
          return selectedOptionalInvoiceLinesField(opts);
        })
        .addField(
          "paymentScheme",
          ["optionalInvoiceLinesSelected"],
          async ({ optionalInvoiceLinesSelected }) => {
            return paymentSchemeChoiceField({
              possiblePaymentSchemes: opts.possiblePaymentSchemes,
              optionalInvoiceLines: optionalInvoiceLinesSelected,
              mandatoryInvoiceLines: opts.mandatoryInvoiceLines,
            });
          },
        )
        .addField(
          "accountInfo",
          ["paymentScheme", "student"],
          async ({ paymentScheme, student }) => {
            return accountInfoField(paymentScheme, student);
          },
        )
        .addField("agreedToTerms", [], () => {
          return agreedToTermsField(opts);
        })
        .addField("confirmedVisualMedia", [], () => {
          return requireConfirmationForVisualMediaPublicityField(opts);
        })
        .build({
          render: (fields) => (
            <section>
              {fields.student()}
              <section style="text-align: right">
                <a
                  href={routes.logoutRoute.link({
                    redirecturl:
                      (opts.target.tag === "lessongroup"
                        ? routes.public_new_enrollment_lessongroupRoute.link({
                            lessongroupid: opts.target.id,
                            trial_lesson: opts.target.trial_lesson,
                          })
                        : routes.public_new_enrollment_privateRoute.link(
                            opts.target,
                          )) + router.makeHashLink({ tag: "New" }),
                  })}
                >
                  {"De persoon die ik wil inschrijven is nog niet gekend"}
                </a>
              </section>

              {fields.questions()}
              {fields.optionalInvoiceLinesSelected()}
              {fields.paymentScheme()}
              {fields.accountInfo()}
              {fields.agreedToTerms()}
              {fields.confirmedVisualMedia()}
            </section>
          ),
        });

      const markRequiredInputsS = new Source(false);
      scheduleForCleanup(
        markRequiredInputsS.observe((val) => {
          if (val === true) {
            touchAllInputs();
          }
        }),
      );

      return (
        <div>
          {form.render()}

          <section style="text-align: right;">
            {renderSaveButton({
              save: async function save() {
                const formValues = form.s.get();
                if (formValues.tag !== "parsed") {
                  markRequiredInputsS.set(true);
                  return {
                    error: "Je mist nog een of meer verplichte velden",
                  };
                } else {
                  markRequiredInputsS.set(false);
                }
                const values = formValues.parsed;

                const res = await rpc(
                  routes.saveNewPublicEnrollmentSpec,
                  undefined,
                  {
                    target: opts.target,
                    questions: values.questions,
                    student: {
                      id: values.student.id,
                      tag: "existing",
                      payment_scheme_id: values.paymentScheme.id,
                      ...values,
                    },
                  },
                );
                return handleSaveNewPublicEnrollmentExistingStudentResult(res);
              },
              timeout: 2000,
              reload: false,
            })}
          </section>
        </div>
      );
    } else {
      return checkAllCasesHandled(mode);
    }
  });
}

export function makeNewStudentDataForm(opts: {
  termsurl: string | null;
  requireConfirmationForVisualMediaPublicity: boolean;
}) {
  const dataForm = new Form({})
    .addField("firstname", [], () =>
      textBox({
        label: "Voornaam",
        validations: [mandatoryValidation, nameValidation],
      }),
    )
    .addField("lastname", [], () =>
      textBox({
        label: "Achternaam",
        validations: [mandatoryValidation, nameValidation],
      }),
    )
    .addField("birthday", [], () =>
      dateBox({
        initialVal: undefined,
        label: "Geboortedatum",
      }),
    )
    .addField("street", [], () =>
      textBox({
        label: "Straatnaam",
        validations: [
          (s) => mandatoryValidation(s, { minNumberOfCharacters: 3 }),
          streetValidation,
        ],
      }),
    )
    .addField("number", [], () =>
      textBox({
        label: "Huisnummer",
        validations: [mandatoryValidation],
      }),
    )
    .addField("bus", [], () =>
      textBox({
        label: "Bus",
      }),
    )
    .addField("zip", [], () =>
      textBox({
        label: "Postcode",
        validations: [mandatoryValidation, zipValidation],
      }),
    )
    .addField("city", [], () =>
      textBox({
        label: "Woonplaats",
        validations: [mandatoryValidation],
      }),
    )
    .addField("phones", [], () => {
      return makeArrayField({
        initialVal: [{ number: "", type: "CellPhone" as const }],
        makeSubForm: async function (t: { number: string; type: phoneType }) {
          return new Form({})
            .addField("number", [], () =>
              textBox({
                initialVal: t.number,
                type: "tel",
                validations: [mandatoryValidation, phoneValidationSimple],
              }),
            )
            .addField("type", [], () =>
              selectBox({
                values: allPhoneTypes,
                initial: t.type,
                show: translatePhoneType,
              }),
            )
            .build();
        },
        makeNew: () => ({
          number: "",
          type: "CellPhone" as const,
        }),
        render: oneLineArrayRender((t) => t.number.trim() === "", {
          minimumOne: true,
        }),
      });
    })
    .addField("comments", [], () =>
      textBox({
        label: "Extra informatie en opmerkingen die je aan ons wilt laten weten.",
        type: "textarea",
      }),
    )
    .addField("agreedToTerms", [], () => {
      return agreedToTermsField(opts);
    })
    .addField("confirmedVisualMedia", [], () => {
      return requireConfirmationForVisualMediaPublicityField(opts);
    })
    .build({
      render: (fields) => (
        <section style="padding: 8px;">
          <h3 style="margin-top: -16px;margin-bottom: 16px; font-weight: 600;">
            {"We hebben nog enkele gegevens nodig..."}
          </h3>
          <h3 style="margin-top: -16px;margin-bottom: 16px; font-style: italic;font-size: 0.9rem;">
            {wrapInParentheses("gegevens van de student")}
          </h3>

          {fields.firstname()}
          {fields.lastname()}
          {fields.birthday()}
          {fields.street()}
          <div className={style.equal_columns_non_responsive}>
            {fields.number()}
            {fields.bus()}
          </div>
          <div className={style.equal_columns_non_responsive}>
            {fields.zip()}
            {fields.city()}
          </div>
          <label>{"Telefoonnummers"}</label>
          {fields.phones()}
          {fields.comments()}
          {fields.agreedToTerms()}
          {fields.confirmedVisualMedia()}
        </section>
      ),
    });
  return dataForm;
}

export function makeNewStudentLoginForm(opts: { showFinancialForm: boolean }) {
  const loginForm = new Form({})
    .addField("email", [], () =>
      textBox({
        label: "Email",
        validations: [mandatoryValidation, emailValidation],
        type: "email",
      }),
    )
    .addField("extra_emails", [], () =>
      makeArrayField({
        initialVal: [],
        makeSubForm: function (t: string) {
          return textBox({
            initialVal: t,
            type: "email",
            validations: [emailValidation],
          });
        },
        makeNew: () => "",
        render: oneLineArrayRender((s) => s.trim() === ""),
      }),
    )
    .build({
      render: (fields) => {
        return (
          <section
            style={
              "padding: 8px;" +
              (opts.showFinancialForm ? "padding-top: 0px" : "")
            }
          >
            {opts.showFinancialForm ? (
              <span></span>
            ) : (
              [
                <h3 style="margin-top: -16px;margin-bottom: 16px; font-weight: 600;">
                  {"... en je inloginformatie"}
                </h3>,
                renderSubtitleSpacer(),
              ]
            )}
            {fields.email()}

            <label>
              {"Extra emailaddressen" +
                " (" +
                "Vader, moeder, ..." +
                ")"}
            </label>
            {fields.extra_emails()}
          </section>
        );
      },
    });
  return loginForm;
}

export function makeNewStudentFinancialForm(
  opts: {
    max_one_optional_line: boolean;
    hide_invoicelines: boolean;
    mandatoryInvoiceLines: (
      | invoicelineFormula
      | invoicelineLG
      | (invoicelineStudent & { type_: invoicelinetype })
    )[];
    optionalInvoiceLines: (
      | invoicelineFormula
      | invoicelineLG
      | (invoicelineStudent & { type_: invoicelinetype })
    )[];
    possiblePaymentSchemes: (
      | periodic_payment_scheme
      | non_periodic_payment_scheme
    )[];
    questions: formulaQuestion[];
  },
  { showFinancialForm }: { showFinancialForm: boolean },
) {
  const financialForm = new Form({})
    .addField("optionalInvoiceLinesSelected", [], async () => {
      return selectedOptionalInvoiceLinesField(opts);
    })
    .addField(
      "familyDiscountComments",
      ["optionalInvoiceLinesSelected"],
      ({ optionalInvoiceLinesSelected }) => {
        if (
          optionalInvoiceLinesSelected.some((il) =>
            il.description.toLowerCase().includes("famil"),
          )
        ) {
          return textBox({
            label: "Naam familielid",
            validations: [mandatoryValidation],
          });
        } else {
          return constantField("");
        }
      },
    )
    .addField(
      "paymentScheme",
      ["optionalInvoiceLinesSelected"],
      async ({ optionalInvoiceLinesSelected }) => {
        return paymentSchemeChoiceField({
          possiblePaymentSchemes: opts.possiblePaymentSchemes,
          optionalInvoiceLines: optionalInvoiceLinesSelected,
          mandatoryInvoiceLines: opts.mandatoryInvoiceLines,
        });
      },
    )
    .addField("accountInfo", ["paymentScheme"], ({ paymentScheme }) => {
      return accountInfoField(paymentScheme, null);
    })
    .build({
      render: (fields) => {
        return (
          <div
            style={showFinancialForm ? "padding: 8px; padding-bottom: 0px" : ""}
          >
            {showFinancialForm
              ? [
                  <h3 style="margin-top: -16px;margin-bottom: 16px; font-weight: 600;">
                    {"... en je financiële en inloginfo"}
                  </h3>,
                  renderSubtitleSpacer(),
                ]
              : []}

            {fields.optionalInvoiceLinesSelected()}
            {fields.familyDiscountComments()}

            {fields.paymentScheme()}
            {fields.accountInfo()}
          </div>
        );
      },
    });

  return financialForm;
}

export async function selectedOptionalInvoiceLinesField(opts: {
  max_one_optional_line: boolean;
  hide_invoicelines: boolean;
  optionalInvoiceLines: (
    | (invoicelineStudent & { type_: invoicelinetype })
    | invoicelineFormula
    | invoicelineLG
    | invoicelineEachPeriod
  )[];
}): Promise<
  Field<
    (invoicelineRequestedOptional & {
      amount:
        | price_formula
        | price_adapted_to_lg
        | price_adapted_to_lg_and_student;
    })[]
  >
> {
  if (
    opts.optionalInvoiceLines.length === 0 ||
    opts.hide_invoicelines === true
  ) {
    return constantField([]);
  }

  const values = opts.optionalInvoiceLines.map((il) => ({
    description: il.description,
    from_each_period_fee:
      "from_each_period_fee" in il ? il.from_each_period_fee : false,
    type_: il.type_,
    // This is not used here anyway, we just save the names of the optional invoicelines and process them later
    amount: "amount" in il ? il.amount : il.amount_with_vat,
  }));

  const field = opts.max_one_optional_line
    ? radioField({
        initial: null,
        values: [[], ...values.map((v) => [v])],
        show: function (t): string {
          const line = t[0];
          if (isNil(line)) {
            return "Geen";
          } else {
            return show(line);
          }
        },
        render: radioFieldCheckboxRenderInputLike,
        label: "Betaling",
      })
    : checkboxesField({
        initial: [],
        values,
        show,
        addNoFilterOption: false,
        label: "Betaling",
        checkboxClass: style.input_like + style.checkbox,
      });

  return { ...field, render: () => <article>{field.render()}</article> };

  function show(il: {
    type_: invoicelinetype;
    description: string;
    amount: currency;
  }) {
    return (
      (il.type_ === "Optional"
        ? ""
        : il.type_ === "Discount"
        ? "Kom je in aanmerking voor "
        : il.type_ === "Normal"
        ? ""
        : checkAllCasesHandled(il.type_)) +
      il.description +
      ` (${renderCurrency(il.amount)})?` +
      (il.type_ === "Discount"
        ? " (" +
          "We checken dit voor alle zekerheid bij het verwerken van je aanvraag" +
          ")"
        : "")
    );
  }
}

export function paymentSchemeChoiceField(opts: {
  mandatoryInvoiceLines: (
    | invoicelineFormula
    | invoicelineLG
    | (invoicelineStudent & { type_: invoicelinetype })
  )[];
  optionalInvoiceLines: {
    amount:
      | price_formula
      | price_adapted_to_lg
      | price_adapted_to_lg_and_student;
  }[];
  possiblePaymentSchemes: (
    | periodic_payment_scheme
    | non_periodic_payment_scheme
  )[];
}): Promise<Field<periodic_payment_scheme | non_periodic_payment_scheme>> {
  const values = opts.possiblePaymentSchemes
    .filter((p) => p.student_can_choose === true)
    .orderBy(
      [(p) => (p.tag === "non_periodic" ? 1 : 2), (p) => p.label],
      ["asc", "asc"],
    );
  if (values.length === 0) {
    return errorField("Techical error: Zero possible payment schemes");
  }
  const amountToPay = opts.mandatoryInvoiceLines
    .reduce(
      (acc, il) =>
        // Not perfect, we should adapt to the student here
        acc.add("amount" in il ? il.amount : il.amount_with_vat),
      currency(0),
    )
    .add(sumCurrency(opts.optionalInvoiceLines.map((il) => il.amount)));
  if (values.length === 1 || amountToPay.value <= 0) {
    return constantField(values[0]);
  }
  return selectBox({
    values,
    initial: values[0],
    show: renderPaymentScheme,
    label: "Hoe wil je betalen?",
  });
}

function handleSaveNewPublicEnrollmentNewStudentResult(
  res: GetReturnTypeFromApiSpec<typeof routes.saveNewPublicEnrollmentSpec>,
):
  | { error: string; tag: "error" }
  | { tag: "success"; text: string; redirectUrl?: string; timeout?: number } {
  if (res.tag === "already_created") {
    return {
      tag: "error",
      error: "Deze inschrijving werd al aangemaakt.",
    };
  } else if (res.tag === "confirmation_email_sent") {
    return {
      tag: "success",
      text: "Gelukt!",
      redirectUrl:
        routes.public_new_enrollment_confirmation_email_sentRoute.link(),
      timeout: 100,
    };
  } else if (res.tag === "student_already_exists") {
    return {
      tag: "error",
      error: "Deze student is al gekend bij ons! Volg onderstaande link om je inschrijving onder je bestaande account op te slaan.",
    };
  } else if (res.tag === "immediately_redirect_to_confirmation") {
    return { tag: "success", text: "", redirectUrl: res.link, timeout: 5 };
  } else if (res.tag === "error") {
    return {
      tag: "error",
      error: res.error,
    };
  } else {
    return checkAllCasesHandled(res);
  }
}

function handleSaveNewPublicEnrollmentExistingStudentResult(
  res: GetReturnTypeFromApiSpec<typeof routes.saveNewPublicEnrollmentSpec>,
):
  | { error: string; tag: "error" }
  | { tag: "success"; text: string; redirectUrl?: string; timeout?: number } {
  if (res.tag === "error") {
    return { tag: "error", error: res.error };
  }
  if (res.tag === "already_created") {
    return {
      tag: "error",
      error: "Deze inschrijving hebben we reeds opgeslagen.",
    };
  }
  if (res.tag === "confirmation_email_sent") {
    // Will never happen
    return {
      tag: "success",
      text: "We stuurden een email naar je emailadres. Klik op de link in die email om je inschrijving te bevestigen!",
    };
  }
  if (res.tag === "student_already_exists") {
    return {
      tag: "error",
      error: "Deze student bestaat volgens ons al.",
    };
  }
  if (res.tag === "immediately_redirect_to_confirmation") {
    return {
      tag: "success",
      text: "Gelukt! Je wordt naar de volgende pagina geleid",
      redirectUrl: res.link,
      timeout: 100,
    };
  }
  return checkAllCasesHandled(res);
}

export async function agreedToTermsField(opts: {
  termsurl: string | null;
}): Promise<Field<boolean>> {
  if (isNil(opts.termsurl)) {
    return constantField(true);
  } else {
    const checkboxfield = await checkBox({
      initialVal: false,
      requiredTrue: true,
      class: style.checkbox + style.input_like,
      label: Array.from(
        stringToJsxClientside(
          "<div>Ik heb %conditions gelezen en goedgekeurd</div>".replace(
            "%conditions",
            (
              <a href={opts.termsurl} target="_blank">
                {"de algemene voorwaarden"}
              </a>
            ).outerHTML,
          ),
        ).childNodes,
      )[0] as HTMLElement,
    });
    return {
      ...checkboxfield,
      render: () => (
        <div>
          <label for="">{"Voorwaarden"}</label>
          {checkboxfield.render()}
        </div>
      ),
    };
  }
}

function requireConfirmationForVisualMediaPublicityField(opts: {
  requireConfirmationForVisualMediaPublicity: boolean;
}): Promise<Field<boolean | null>> {
  if (opts.requireConfirmationForVisualMediaPublicity === true) {
    return checkBox({
      style: "display: block; margin-bottom: 16px;",
      initialVal: false,
      label: "Ik ga akkoord met het gebruik van beeldmateriaal van de leerling voor publicitaire doeleinden",
    });
  } else {
    return constantField(null);
  }
}

export async function accountInfoField(
  paymentScheme: periodic_payment_scheme | non_periodic_payment_scheme,
  student: { id: studentid } | null,
): Promise<Field<accountInfoClient>> {
  if (
    paymentScheme.tag === "periodic" &&
    paymentScheme.periodic_payment_method === "direct_debit"
  ) {
    const stu = student
      ? await rpc(routes.getStudentFinancialDetailsSpec, undefined, {
          id: student.id,
        })
      : null;
    return new Form({})
      .addField("tag", [], () => constantField("applicable" as const))
      .addField(
        "iban",
        [],
        () =>
          textBox({
            initialVal: stu?.iban || "",
            label: "IBAN rekening",
            validations: [ibanValidation],
          }) as Promise<Field<iban>>,
      )
      .addField("name_on_iban", [], () =>
        textBox({
          initialVal: stu?.name_on_iban || "",
          label: "Naam op rekening",
          validations: [mandatoryValidation],
        }),
      )
      .build();
  } else {
    return constantField({ tag: "not_applicable" });
  }
}

function renderSubtitleSpacer() {
  return (
    <h3
      className={style.remove_on_phone}
      style="margin-top: -16px; margin-bottom: 16px; font-size: 0.9rem;"
    >
      &nbsp;
    </h3>
  );
}
